import React, { useState, useEffect, Fragment } from "react";
import PropTypes from "prop-types";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import {
  ArrowLeft,
  Save,
  X,
  Plus,
  Trash,
  FileText,
  Upload,
  Download,
  Eye,
  Edit,
  Calendar,
  CheckCircle,
  AlertCircle,
} from "lucide-react";

import {
  getActiveWaterQuality,
  getAllWaterQuality,
  getWaterQualityById,
  createWaterQuality,
  updateWaterQuality,
  deleteWaterQuality,
  uploadWaterQualityDocument,
  clearWaterQuality,
} from "../../actions/waterQuality";

const API_URL = process.env.REACT_APP_SERVER_API_URL;

const initialState = {
  name: "",
  month: "",
  active: false,
};

const WaterQuality = ({
  getActiveWaterQuality,
  getAllWaterQuality,
  getWaterQualityById,
  createWaterQuality,
  updateWaterQuality,
  deleteWaterQuality,
  uploadWaterQualityDocument,
  clearWaterQuality,
  waterQuality,
  loading,
  assetPath,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState(initialState);
  const [showForm, setShowForm] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  // Check if we're on the new route or have an id parameter
  const isNewRecord = location.pathname.endsWith('/new');
  const isEditMode = id && id !== "new";


  useEffect(() => {
    document.title = isNewRecord ? 'Create Water Quality Report' : isEditMode ? 'Edit Water Quality Report' : 'Water Quality Reports';
    
    if (isNewRecord) {
      clearWaterQuality();
      setFormData(initialState);
    } else if (isEditMode) {
      getWaterQualityById(id);
    } else {
      getAllWaterQuality();
    }
  }, [location.pathname, id, isNewRecord, isEditMode, clearWaterQuality, getWaterQualityById, getAllWaterQuality]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('.dropdown')) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  useEffect(() => {
    if (waterQuality.currentWaterQuality && isEditMode) {
      setFormData({
        name: waterQuality.currentWaterQuality.name || "",
        month: waterQuality.currentWaterQuality.month || "",
        active: waterQuality.currentWaterQuality.active || false,
      });
    }
  }, [waterQuality.currentWaterQuality, isEditMode]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        alert('Please select a PDF file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        alert('File size should be less than 10MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isNewRecord) {
      createWaterQuality(formData);
      navigate('/inventory/v1.2/water-quality');
    } else if (isEditMode) {
      updateWaterQuality(id, formData);
      navigate('/inventory/v1.2/water-quality');
    }
  };

  const handleDelete = (recordId) => {
    if (window.confirm('Are you sure you want to delete this water quality record?')) {
      deleteWaterQuality(recordId);
    }
  };

  const toggleDropdown = (reportId) => {
    setOpenDropdown(openDropdown === reportId ? null : reportId);
  };

  const closeDropdown = () => {
    setOpenDropdown(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    
    setUploading(true);
    try {
      await uploadWaterQualityDocument(id, selectedFile);
      setSelectedFile(null);
      setShowUploadForm(false);
      getWaterQualityById(id); // Refresh the record
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getDocumentUrl = (documentPath) => {
    if (!documentPath) return null;
    if (documentPath.startsWith('http')) return documentPath;
    return `${assetPath}${documentPath}`;
  };

  if (isNewRecord || isEditMode) {
    return (
      <Fragment>
        <div className="container-fluid py-4">
          {/* Header */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center">
                <Link
                  className="btn btn-outline-primary d-flex align-items-center"
                  to="/inventory/v1.2/water-quality"
                >
                  <ArrowLeft size={16} className="me-2" />
                  Back to Water Quality
                </Link>
                <h2 className="text-primary mb-0 fw-bold">
                  {isNewRecord ? "Create Water Quality Report" : `Edit Water Quality Report`}
                </h2>
                <div></div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="row">
              {/* Main Content */}
              <div className="col-lg-8">
                <div className="card shadow-sm mb-4">
                  <div className="card-header bg-primary text-white">
                    <h5 className="mb-0 d-flex align-items-center">
                      <FileText className="me-2" size={20} />
                      Water Quality Information
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-bold">Report Name</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className="form-control form-control-lg"
                          placeholder="Enter report name"
                          required
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-bold">Month</label>
                        <input
                          type="text"
                          name="month"
                          value={formData.month}
                          onChange={handleChange}
                          className="form-control form-control-lg"
                          placeholder="e.g., January 2024"
                          required
                        />
                      </div>
                      <div className="col-12 mb-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            name="active"
                            checked={formData.active}
                            onChange={handleChange}
                            id="activeCheck"
                          />
                          <label className="form-check-label fw-bold" htmlFor="activeCheck">
                            Set as Active Report
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Document Upload Section (Edit Mode Only) */}
                {isEditMode && waterQuality.currentWaterQuality && (
                  <div className="card shadow-sm mb-4">
                    <div className="card-header bg-info text-white d-flex justify-content-between align-items-center">
                      <h5 className="mb-0 d-flex align-items-center">
                        <Upload className="me-2" size={20} />
                        Document Management
                      </h5>
                      <button
                        type="button"
                        className="btn btn-light btn-sm"
                        onClick={() => setShowUploadForm(!showUploadForm)}
                      >
                        <Upload size={16} className="me-1" />
                        {showUploadForm ? 'Hide Upload' : 'Upload Document'}
                      </button>
                    </div>
                    <div className="card-body">
                      {showUploadForm ? (
                        <div className="row">
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label fw-bold">Upload PDF Document</label>
                              <input
                                type="file"
                                className="form-control"
                                accept=".pdf"
                                onChange={handleFileSelect}
                                disabled={uploading}
                              />
                              {selectedFile && (
                                <div className="mt-2">
                                  <small className="text-success">
                                    <CheckCircle size={14} className="me-1" />
                                    File selected: {selectedFile.name}
                                  </small>
                                  <button
                                    type="button"
                                    className="btn btn-primary btn-sm ms-2"
                                    onClick={handleUpload}
                                    disabled={uploading}
                                  >
                                    {uploading ? 'Uploading...' : 'Upload'}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="alert alert-info">
                              <AlertCircle size={16} className="me-2" />
                              <strong>File Requirements:</strong>
                              <ul className="mb-0 mt-2">
                                <li>PDF format only</li>
                                <li>Maximum size: 10MB</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-3">
                          {waterQuality.currentWaterQuality.document ? (
                            <div className="row align-items-center">
                              <div className="col-md-8">
                                <div className="mb-2">
                                  <strong>Current Document:</strong>
                                  <span className="text-truncate d-block small text-muted">
                                    {waterQuality.currentWaterQuality.document}
                                  </span>
                                </div>
                                <div className="mt-3">
                                  <a
                                    href={getDocumentUrl(waterQuality.currentWaterQuality.document)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-outline-primary btn-sm me-2"
                                  >
                                    <Eye size={16} className="me-2" />
                                    View Document
                                  </a>
                                  <a
                                    href={getDocumentUrl(waterQuality.currentWaterQuality.document)}
                                    download
                                    className="btn btn-outline-success btn-sm"
                                  >
                                    <Download size={16} className="me-2" />
                                    Download
                                  </a>
                                </div>
                              </div>
                              <div className="col-md-4">
                                <div className="text-center">
                                  <FileText size={48} className="text-primary" />
                                </div>
                              </div>
                            </div>
                          ) : (
                            <p className="text-muted">No document uploaded yet</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="col-lg-4">
                <div className="card shadow-sm">
                  <div className="card-body text-center">
                    <button
                      type="submit"
                      className="btn btn-primary btn-lg w-100"
                      disabled={isEditMode ? loading : false}
                    >
                      <Save size={16} className="me-2" />
                      {isNewRecord ? "Create Report" : "Update Report"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </Fragment>
    );
  }

  // List View
  return (
    <Fragment>
      <div className="container-fluid py-4">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center">
              <h2 className="text-primary mb-0 fw-bold">
                <FileText className="me-2" size={24} />
                Water Quality Reports
              </h2>
              <Link
                className="btn btn-primary d-flex align-items-center"
                to="/inventory/v1.2/water-quality/new"
              >
                <Plus size={16} className="me-2" />
                Add New Report
              </Link>
            </div>
          </div>
        </div>

        {/* Active Report */}
        {waterQuality.activeWaterQuality && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border-success">
                <div className="card-header bg-success text-white">
                  <h5 className="mb-0 d-flex align-items-center">
                    <CheckCircle className="me-2" size={20} />
                    Active Report
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row align-items-center">
                    <div className="col-md-8">
                      <h6 className="card-title">{waterQuality.activeWaterQuality.name}</h6>
                      <p className="text-muted mb-2">{waterQuality.activeWaterQuality.month}</p>
                      <div className="d-flex align-items-center">
                        <span className="badge bg-success me-2">Active</span>
                        {waterQuality.activeWaterQuality.document && (
                          <a
                            href={getDocumentUrl(waterQuality.activeWaterQuality.document)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-outline-primary btn-sm me-2"
                          >
                            <Eye size={14} className="me-1" />
                            View
                          </a>
                        )}
                        <Link
                          to={`/inventory/v1.2/water-quality/${waterQuality.activeWaterQuality._id}`}
                          className="btn btn-outline-secondary btn-sm"
                        >
                          <Edit size={14} className="me-1" />
                          Edit
                        </Link>
                      </div>
                    </div>
                    <div className="col-md-4 text-end">
                      <div className="text-muted small">
                        Created: {formatDate(waterQuality.activeWaterQuality.createdAt)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* All Reports */}
        <div className="row">
          <div className="col-12">
            <div className="card shadow-sm">
              <div className="card-header bg-secondary text-white">
                <h5 className="mb-0 d-flex align-items-center">
                  <Calendar className="me-2" size={20} />
                  All Reports ({waterQuality.allWaterQuality?.length || 0})
                </h5>
              </div>
              <div className="card-body">
                {loading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : waterQuality.allWaterQuality && waterQuality.allWaterQuality.length > 0 ? (
                  <div className="row">
                    {waterQuality.allWaterQuality.map((report) => (
                      <div key={report._id} className="col-md-6 mb-3">
                        <div className="card border">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div className="flex-grow-1">
                                <h6 className="card-title mb-1">{report.name}</h6>
                                <p className="text-muted small mb-2">{report.month}</p>
                                <div className="d-flex align-items-center">
                                  <span className={`badge ${report.active ? 'bg-success' : 'bg-secondary'} me-2`}>
                                    {report.active ? 'Active' : 'Inactive'}
                                  </span>
                                  {report.document && (
                                    <a
                                      href={getDocumentUrl(report.document)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="btn btn-outline-primary btn-sm me-2"
                                    >
                                      <Eye size={12} className="me-1" />
                                      View
                                    </a>
                                  )}
                                </div>
                              </div>
                              <div className="dropdown position-relative">
                                <button
                                  className="btn btn-outline-secondary btn-sm dropdown-toggle"
                                  type="button"
                                  onClick={() => toggleDropdown(report._id)}
                                >
                                  Actions
                                </button>
                                {openDropdown === report._id && (
                                  <ul className="dropdown-menu show position-absolute" style={{ top: '100%', right: '0', zIndex: 1000 }}>
                                    <li>
                                      <Link
                                        className="dropdown-item"
                                        to={`/inventory/v1.2/water-quality/${report._id}`}
                                        onClick={closeDropdown}
                                      >
                                        <Edit size={14} className="me-2" />
                                        Edit
                                      </Link>
                                    </li>
                                    <li>
                                      <button
                                        className="dropdown-item text-danger"
                                        onClick={() => {
                                          closeDropdown();
                                          handleDelete(report._id);
                                        }}
                                      >
                                        <Trash size={14} className="me-2" />
                                        Delete
                                      </button>
                                    </li>
                                  </ul>
                                )}
                              </div>
                            </div>
                            <div className="small text-muted">
                              Created: {formatDate(report.createdAt)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted">
                    <FileText size={48} className="mb-3" />
                    <p>No water quality reports found.</p>
                    <Link
                      to="/inventory/v1.2/water-quality/new"
                      className="btn btn-primary"
                    >
                      <Plus size={16} className="me-2" />
                      Create First Report
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

WaterQuality.propTypes = {
  getActiveWaterQuality: PropTypes.func.isRequired,
  getAllWaterQuality: PropTypes.func.isRequired,
  getWaterQualityById: PropTypes.func.isRequired,
  createWaterQuality: PropTypes.func.isRequired,
  updateWaterQuality: PropTypes.func.isRequired,
  deleteWaterQuality: PropTypes.func.isRequired,
  uploadWaterQualityDocument: PropTypes.func.isRequired,
  clearWaterQuality: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  assetPath: PropTypes.string.isRequired,
  waterQuality: PropTypes.object,
};

const mapStateToProps = (state) => ({
  assetPath: state.waterQuality.assetPath,
  loading: state.waterQuality.loading,
  waterQuality: state.waterQuality,
});

export default connect(mapStateToProps, {
  getActiveWaterQuality,
  getAllWaterQuality,
  getWaterQualityById,
  createWaterQuality,
  updateWaterQuality,
  deleteWaterQuality,
  uploadWaterQualityDocument,
  clearWaterQuality,
})(WaterQuality);
